import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Contract, ActiveContract } from "../../../../client";
import { faCircleCheck } from "@fortawesome/free-regular-svg-icons";
import { faCircleXmark, faFileLines } from "@fortawesome/free-solid-svg-icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "../../../../components";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { cn } from "../../../../components/utils/cn";

interface Props {
  size: "small" | "medium" | "large";
  contract?: Contract | null;
  availabilityDate?: string | null;
}

export const AvailabilityPill = ({ contract, availabilityDate }: Props) => {
  const dateSource = availabilityDate ?? contract?.endDate;
  const label = dateSource ? formatAvailableDate(dateSource) : "Available now";

  return (
    <div className="flex flex-row rounded-3xl bg-red-1 text-red-3 text-md items-center justify-center gap-1 p-2 font-semibold">
      <FontAwesomeIcon icon={faCircleCheck} className="text-red-2" />
      {label}
    </div>
  );
};

interface AvailabilityTagProps {
  availabilityDate?: string | null;
  activeContract?: ActiveContract | null;
  compact?: boolean;
}

export const AvailabilityTag = ({ availabilityDate, activeContract, compact = false }: AvailabilityTagProps) => {
  const dateSource = availabilityDate ?? activeContract?.contract?.endDate ?? null;
  const label = dateSource ? formatAvailableDate(dateSource) : "Available now";

  const isNow = label === "Available now";
  const isOver3 = label === "Available in > 3 months";

  const pillClass = cn(
    "flex flex-row rounded-3xl items-center justify-center gap-1.5 px-3 py-1 text-xs font-semibold border",
    isNow && "bg-green-50 border-green-200 text-green-500",
    !isNow && !isOver3 && "bg-amber-50 border-amber-200 text-amber-500",
    isOver3 && "bg-red-50 border-red-200 text-red-400",
  );

  const displayText = isNow
    ? "Available now"
    : compact
      ? label.replace("Available in ", "").replace("Available in > 3 months", "> 3 months")
      : label;

  const pill = (
    <div className={pillClass}>
      <FontAwesomeIcon
        icon={isOver3 ? faCircleXmark : faCircleCheck}
        className="w-3.5 h-3.5 shrink-0"
      />
      {displayText}
    </div>
  );

  if (!dateSource) return pill;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>{pill}</TooltipTrigger>
        <TooltipContent>{dateSource}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const formatAvailableDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.ceil(
      (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24),
    );

    const nowY = now.getFullYear();
    const nowM = now.getMonth();
    const dateY = date.getFullYear();
    const dateM = date.getMonth();
    const monthDiff = (dateY - nowY) * 12 + (dateM - nowM);
    const isExactMonthDay = date.getDate() === now.getDate();

    if (diffInDays <= 0) return "Available now";
    if (monthDiff === 0) return "Available in < 1 month";
    if (monthDiff === 1 && isExactMonthDay) return "Available in 1 month";
    if (monthDiff === 1) return "Available in < 2 months";
    if (monthDiff === 2 && isExactMonthDay) return "Available in 2 months";
    if (monthDiff === 2) return "Available in < 3 months";
    if (monthDiff === 3 && isExactMonthDay) return "Available in 3 months";
    return "Available in > 3 months";
  } catch {
    return "Available now";
  }
};

interface ContractTagProps {
  activeContract: ActiveContract;
}

export const ContractTag = ({ activeContract }: ContractTagProps) => {
  const { contract, client } = activeContract;
  const clientName = client?.name ?? contract.companyName ?? "client";

  const endDate = contract.endDate ? new Date(contract.endDate) : null;
  const now = new Date();

  let isWithin6Months = false;
  let monthsUntilEnd = 0;

  if (endDate && endDate > now) {
    const nowY = now.getFullYear();
    const nowM = now.getMonth();
    monthsUntilEnd = (endDate.getFullYear() - nowY) * 12 + (endDate.getMonth() - nowM);
    isWithin6Months = monthsUntilEnd <= 6;
  }

  const isAvailableSoon = endDate !== null && endDate > now && isWithin6Months;

  const pillClass = cn(
    "flex flex-row rounded-3xl items-center justify-center gap-1.5 px-3 py-1 text-xs font-semibold border whitespace-nowrap",
    isAvailableSoon
      ? "bg-amber-50 border-amber-200 text-amber-500"
      : "bg-red-50 border-red-200 text-red-400",
  );

  let text: string;
  let tooltipText: string;

  if (isAvailableSoon && endDate) {
    const monthLabel = monthsUntilEnd <= 1 ? "< 1 month" : `${monthsUntilEnd} months`;
    text = `Available in ${monthLabel}`;
    tooltipText = `Contract with ${clientName} until ${endDate.toLocaleDateString(undefined, { dateStyle: "medium" })}`;
  } else {
    text = "Active contract";
    tooltipText = endDate
      ? `Contract with ${clientName} until ${endDate.toLocaleDateString(undefined, { dateStyle: "medium" })}`
      : `Contract with ${clientName}`;
  }

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={pillClass}>
            <FontAwesomeIcon icon={faFileLines} className="w-3.5 h-3.5 shrink-0" />
            {text}
          </div>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
